import React from 'react';

// Which wallet paid for an item.
//
// The tracker knows what you paid for a long time. What it never knew is where
// the money came from, so buying a $400 knife left your cash balance sitting
// exactly where it was and the wallet slowly became fiction. This is the missing
// field.
//
// WHAT SELECTING A SOURCE ACTUALLY DOES
// Only wallets nobody can read are adjusted. CSFloat is read from your account
// by the extension, and a crypto wallet with addresses configured is read from
// the chains, and both of those readings were taken AFTER the money moved, so
// they already contain the purchase. Deducting again would double count it. The
// server owns that rule (services/walletSources.js); picking CSFloat here is
// therefore a note about where the money went, not an instruction to move it.
//
// "Not tracked" is a first-class answer, not an opt-out. Items arrive from
// trades, from openings, from before you started tracking, and forcing every one
// of those to name a wallet would put noise into the only figures worth trusting.

export const PAID_WITH_OPTIONS = [
  { id: 'bank',    label: 'Cash' },
  { id: 'crypto',  label: 'Crypto' },
  { id: 'csfloat', label: 'CSFloat' },
  { id: null,      label: 'Not tracked' },
];

// The inactive colours come from custom properties rather than being written in,
// so a themed container can hand this control its own palette without any prop
// drilling. The fallbacks are the app's dark chrome, which is what every item
// form wants; the Zone paper theme and the trade-boot modal override them on an
// ancestor, because dark-theme greys on cream paper are invisible.
const MUTE    = 'var(--pw-mute, var(--text-muted))';
const LINE    = 'var(--pw-line, rgba(255,255,255,0.12))';
const SURFACE = 'var(--pw-surface, rgba(255,255,255,0.04))';

/**
 * @param {string|null} value     current source
 * @param {(next: string|null) => void} onChange
 * @param {string} [accent]       hex accent for the active pill
 * @param {boolean} [zone]        squared-off styling for the Zone board
 * @param {boolean} [allowUntracked]  offer "Not tracked". False where a source
 *        is required, e.g. cash that demonstrably changed hands in a trade.
 * @param {boolean} [allowBank]  offer Cash. False when the user has switched the
 *        hand-typed cash wallet off, since paying from a wallet that is not
 *        shown anywhere would move a figure they can never see.
 */
export default function PaidWith({
  value, onChange, accent = '#22c55e', zone = false,
  allowUntracked = true, allowBank = true,
}) {
  const options = PAID_WITH_OPTIONS.filter(o =>
    (allowUntracked || o.id !== null) && (allowBank || o.id !== 'bank'));

  return (
    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' }}>
      {options.map(option => {
        const active = (value ?? null) === option.id;
        return (
          <button
            key={option.label}
            type="button"
            onClick={() => onChange(option.id)}
            style={{
              padding: '3px 10px',
              borderRadius: zone ? 0 : 999,
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontSize: '0.9rem',
              fontWeight: 600,
              border: `1px solid ${active ? accent + '8c' : LINE}`,
              background: active ? accent + '22' : SURFACE,
              color: active ? accent : MUTE,
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/** The label plus the pills, which is how all four item forms use it. */
export function PaidWithField({ value, onChange, accent, zone, allowBank, labelClass = 'ialt-label' }) {
  return (
    <div>
      <div className={labelClass}>Paid with</div>
      <PaidWith value={value} onChange={onChange} accent={accent} zone={zone} allowBank={allowBank} />
    </div>
  );
}
